import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../service/Api';
import Drawer from '../common/Drawer';
import { Users, Plus, Trash2, X, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

export default function ProspectsSidePanel({ isOpen, onClose, onProspectsUpdate }) {
  const { user } = useAuth();
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    domain: ''
  });

  const isAdmin = user?.role == 'superadmin' || user?.role == 'admin';

  useEffect(() => {
    if (isOpen) {
      loadProspects();
    }
  }, [isOpen]);

  const loadProspects = async () => {
    try {
      setLoading(true);
      const response = await apiService.getTailspinProspects();
      if (response.data.success) {
        const prospectData = response.data.data || {};
        const items = Array.isArray(prospectData) ? prospectData : prospectData.items || [];
        setProspects(items);
      } else {
        toast.error('Failed to load prospects');
      }
    } catch (error) {
      console.error('Error loading prospects:', error);
      toast.error('Error loading prospects');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin || creating) return;

    const { name, domain } = formData;
    if (!name || !domain) {
      toast.error('Name and domain are required');
      return;
    }

    setCreating(true);
    try {
      const response = await apiService.createTailspinProspect({
        name,
        domain
      });

      if (response.data.success) {
        toast.success('Prospect created successfully');
        setFormData({ name: '', domain: '' });
        setShowCreateForm(false);
        // Reload prospects list in side panel
        await loadProspects();
        // Update dashboard count without full reload
        if (onProspectsUpdate) {
          onProspectsUpdate();
        }
      } else {
        toast.error(response.data.message || 'Failed to create prospect');
      }
    } catch (error) {
      console.error('Error creating prospect:', error);
      toast.error('Error creating prospect');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteClick = (prospectId) => {
    if (!isAdmin) return;
    setConfirmDeleteId(prospectId);
  };

  const handleCancelDelete = () => {
    setConfirmDeleteId(null);
  };

  const handleConfirmDelete = async (prospectId) => {
    if (!isAdmin) return;
    
    setDeletingId(prospectId);
    try {
      const response = await apiService.deleteTailspinProspect(prospectId);
      
      if (response.data.success) {
        toast.success('Prospect deleted successfully');
        setConfirmDeleteId(null);
        // Update local state optimistically
        setProspects(prevProspects => prevProspects.filter(p => p.id !== prospectId));
        // Update dashboard count without full reload
        if (onProspectsUpdate) {
          onProspectsUpdate();
        }
      } else {
        toast.error(response.data.message || 'Failed to delete prospect');
        // Reload on error to get correct state
        await loadProspects();
      }
    } catch (error) {
      console.error('Error deleting prospect:', error);
      toast.error('Error deleting prospect');
      // Reload on error to get correct state
      await loadProspects();
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Prospects"
      description="View and manage your prospects"
      size="lg"
    >
      <div className="flex flex-col h-full">
        {/* Header Actions */}
    
          <div className="mb-4">
            {!showCreateForm ? (
              <button
                onClick={() => setShowCreateForm(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
                style={{ 
                  backgroundColor: '#FF6B5A', 
                  color: 'white' 
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              >
                <Plus className="w-4 h-4" />
                <span>Create Prospect</span>
              </button>
            ) : (
              <div className="border rounded-lg p-4" style={{ borderColor: '#D7B472', backgroundColor: '#F8F7F4' }}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold" style={{ color: '#0A0A10' }}>Create New Prospect</h3>
                  <button
                    onClick={() => {
                      setShowCreateForm(false);
                      setFormData({ name: '', domain: '' });
                    }}
                    className="p-1 rounded hover:bg-gray-200"
                  >
                    <X className="w-4 h-4" style={{ color: '#0A0A10' }} />
                  </button>
                </div>
                <form onSubmit={handleCreateSubmit} className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: '#0A0A10' }}>
                      Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      style={{ borderColor: '#D7B472', backgroundColor: 'white' }}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: '#0A0A10' }}>
                      Domain *
                    </label>
                    <input
                      type="text"
                      value={formData.domain}
                      onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      style={{ borderColor: '#D7B472', backgroundColor: 'white' }}
                      required
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={creating}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                      style={{ backgroundColor: '#FF6B5A', color: 'white' }}
                    >
                      {creating ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Creating...</span>
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4" />
                          <span>Create</span>
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateForm(false);
                        setFormData({ name: '', domain: '' });
                      }}
                      className="px-4 py-2 rounded-lg border transition-colors"
                      style={{ borderColor: '#D7B472', color: '#0A0A10' }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
     

        {/* Prospects List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#FF6B5A' }} />
            </div>
          ) : prospects.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto mb-3" style={{ color: '#0A0A10', opacity: 0.5 }} />
              <p style={{ color: '#0A0A10', opacity: 0.7 }}>No prospects yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {prospects.map((prospect) => (
                <div
                  key={prospect.id}
                  className="border rounded-lg p-4"
                  style={{ borderColor: '#D7B472', backgroundColor: '#F8F7F4' }}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="font-semibold mb-1" style={{ color: '#0A0A10' }}>
                        {prospect.name}
                      </h3>
                      {prospect.domain && (
                        <p className="text-sm mb-2" style={{ color: '#0A0A10', opacity: 0.7 }}>
                          {prospect.domain}
                        </p>
                      )}
                    </div>
                    {isAdmin && deletingId !== prospect.id && (
                      <button
                        onClick={() => handleDeleteClick(prospect.id)}
                        className="p-1 rounded hover:bg-gray-200 transition-colors"
                        disabled={deletingId === prospect.id}
                        title="Delete prospect"
                      >
                        <Trash2 className="w-4 h-4" style={{ color: '#FF6B5A' }} />
                      </button>
                    )}
                  </div>

                  {confirmDeleteId === prospect.id ? (
                    <div className="mt-3 p-3 border rounded-lg" style={{ borderColor: '#FF6B5A', backgroundColor: 'rgba(255, 107, 90, 0.1)' }}>
                      <p className="text-sm mb-3" style={{ color: '#0A0A10' }}>
                        Are you sure you want to delete "{prospect.name}"? This action cannot be undone.
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleConfirmDelete(prospect.id)}
                          disabled={deletingId === prospect.id}
                          className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors disabled:opacity-50"
                          style={{ backgroundColor: '#FF6B5A', color: 'white' }}
                        >
                          {deletingId === prospect.id ? (
                            <>
                              <Loader2 className="w-3 h-3 animate-spin" />
                              <span>Deleting...</span>
                            </>
                          ) : (
                            <>
                              <Trash2 className="w-3 h-3" />
                              <span>Delete</span>
                            </>
                          )}
                        </button>
                        <button
                          onClick={handleCancelDelete}
                          disabled={deletingId === prospect.id}
                          className="px-3 py-1.5 rounded-lg text-sm border transition-colors disabled:opacity-50"
                          style={{ borderColor: '#D7B472', color: '#0A0A10' }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
}

